// The ONE declarative description of every table the seven R1.1 / R1.2 categories add (Guide §7.2).
// Everything else is derived from it: Prisma models + SQL (packages/db/scripts/gen-modules.mjs), zod op schemas (./schemas.ts),
// the generic server applier, the pull/snapshot registry, the phone's IndexedDB stores and the schema-driven forms.
// Rules from Guide §7.2: shopId, UUIDv7 ids, Decimal money, Row Level Security, version + fieldTs for sync — all added by the generator.
import type { Category } from '../enums';
import type { PermissionKey } from '../permissions';

export type FieldType =
  | 'text' // short string
  | 'note' // long string
  | 'money' // Decimal(14,2)
  | 'qty' // Decimal(14,3)
  | 'int'
  | 'date' // YYYY-MM-DD
  | 'datetime' // ISO with offset, minute precision matters (MFS)
  | 'bool'
  | 'enum'
  | 'ref' // uuid of another module / core table (same shop)
  | 'phone'
  | 'json';

export interface FieldSpec {
  key: string;
  type: FieldType;
  /** [Bangla, English] label shown on forms and lists */
  l: readonly [string, string];
  req?: boolean;
  max?: number;
  options?: readonly string[];
  /** [Bangla, English] label per option (same order as options) */
  optionLabels?: ReadonlyArray<readonly [string, string]>;
  /** for `ref`: the table (= sync entity) the id points to */
  ref?: string;
  def?: string | number | boolean | null;
  /** show on the list row / used as an IndexedDB index */
  idx?: boolean;
  /** column the client never sends: maintained by the server (balances, void marks, biz dates) */
  server?: boolean;
  /** hint under the input */
  hint?: readonly [string, string];
}

export interface EntitySpec {
  /** sync entity == table name, e.g. `mfs_wallets` */
  entity: string;
  /** Prisma model name */
  model: string;
  /** `*.upsert` operation name (generic documents) */
  op: string;
  /** `doc`: generic upsert + soft delete. `ledger`: append-only rows written by dedicated ops (money effects) */
  kind: 'doc' | 'ledger';
  category: Category;
  /** Bible 3.0 tool key that gates writes on Basic (null = not gated) */
  tool: string | null;
  perm: PermissionKey;
  labels: readonly [string, string];
  titleField: string;
  /** date-ish column the snapshot is windowed on (high-volume ledgers only) */
  window?: string;
  fields: readonly FieldSpec[];
}

const f = (key: string, type: FieldType, bn: string, en: string, o: Partial<Omit<FieldSpec, 'key' | 'type' | 'l'>> = {}): FieldSpec => ({ key, type, l: [bn, en], ...o });
const opt = (options: readonly string[], labels: ReadonlyArray<readonly [string, string]>) => ({ options, optionLabels: labels });
const DOC = 'doc' as const;

// ── Mobile Banking & Recharge (R1.1) ─────────────────────────────────────────
export const MFS_PROVIDERS = ['bkash', 'nagad', 'rocket', 'upay', 'other'] as const;
export const MFS_TXN_TYPES = ['cash_in', 'cash_out', 'send', 'payment'] as const;
export const RECHARGE_OPERATORS = ['gp', 'robi', 'banglalink', 'teletalk', 'airtel', 'skitto'] as const;
const PROVIDER_LABELS = [['bKash', 'bKash'], ['Nagad', 'Nagad'], ['Rocket', 'Rocket'], ['Upay', 'Upay'], ['অন্যান্য', 'Other']] as const;
export const MFS_TXN_LABELS: Record<(typeof MFS_TXN_TYPES)[number], readonly [string, string]> = {
  cash_in: ['ক্যাশ ইন', 'Cash In'], cash_out: ['ক্যাশ আউট', 'Cash Out'], send: ['সেন্ড মানি', 'Send money'], payment: ['পেমেন্ট', 'Payment'],
};
export const OPERATOR_LABELS: Record<(typeof RECHARGE_OPERATORS)[number], readonly [string, string]> = {
  gp: ['গ্রামীণফোন', 'Grameenphone'], robi: ['রবি', 'Robi'], banglalink: ['বাংলালিংক', 'Banglalink'], teletalk: ['টেলিটক', 'Teletalk'], airtel: ['এয়ারটেল', 'Airtel'], skitto: ['স্কিটো', 'Skitto'],
};

// ── all specs ────────────────────────────────────────────────────────────────
export const MODULE_ENTITIES: readonly EntitySpec[] = [
  // Mobile Banking ----------------------------------------------------------
  {
    entity: 'mfs_wallets', model: 'MfsWallet', op: 'mfs_wallet.upsert', kind: DOC, category: 'mobile_banking', tool: 'multi_mfs_balance', perm: 'stock_edit', labels: ['MFS ওয়ালেট', 'MFS wallets'], titleField: 'number',
    fields: [
      f('provider', 'enum', 'প্রোভাইডার', 'Provider', { req: true, idx: true, ...opt(MFS_PROVIDERS, PROVIDER_LABELS) }),
      f('number', 'text', 'নম্বর', 'Number', { req: true, max: 20 }),
      f('label', 'text', 'নাম (ঐচ্ছিক)', 'Label (optional)', { max: 40 }),
      f('opening', 'money', 'শুরুর ব্যালেন্স (ফ্লোট)', 'Opening float', { def: '0' }),
      f('balance', 'money', 'বর্তমান ব্যালেন্স', 'Balance', { def: '0', server: true }),
      f('lowAlert', 'money', 'কম হলে সতর্কতা (৳)', 'Alert below (৳)', { def: '0', hint: ['এর নিচে নামলে হোমে সতর্কতা দেখাবে', 'Home warns when the float drops below this'] }),
      f('cashInRate', 'money', 'ক্যাশ ইন কমিশন (প্রতি ১০০০)', 'Cash-in commission (per 1,000)', { def: '0' }),
      f('cashOutRate', 'money', 'ক্যাশ আউট কমিশন (প্রতি ১০০০)', 'Cash-out commission (per 1,000)', { def: '0' }),
      f('isActive', 'bool', 'চালু', 'Active', { def: true }),
    ],
  },
  {
    entity: 'mfs_txns', model: 'MfsTxn', op: 'mfs_txn.create', kind: 'ledger', category: 'mobile_banking', tool: 'multi_mfs_balance', perm: 'sale_entry', labels: ['Cash In/Out', 'Cash In/Out'], titleField: 'type', window: 'biz_date',
    fields: [
      f('walletId', 'ref', 'ওয়ালেট', 'Wallet', { req: true, ref: 'mfs_wallets', idx: true }),
      f('type', 'enum', 'ধরন', 'Type', { req: true, idx: true, ...opt(MFS_TXN_TYPES, MFS_TXN_TYPES.map((k) => MFS_TXN_LABELS[k])) }),
      f('amount', 'money', 'অঙ্ক', 'Amount', { req: true }),
      f('commission', 'money', 'কমিশন', 'Commission', { def: '0' }),
      f('customerNumber', 'text', 'কাস্টমারের নম্বর', 'Customer number', { max: 20 }),
      f('trxId', 'text', 'Transaction ID (ঐচ্ছিক)', 'Transaction ID (optional)', { max: 40 }),
      f('source', 'enum', 'উৎস', 'Source', { def: 'manual', ...opt(['manual', 'imported'], [['হাতে', 'Manual'], ['Import', 'Imported']]) }),
      f('occurredAt', 'datetime', 'সময় (মিনিট পর্যন্ত)', 'Time (to the minute)', { req: true, idx: true }),
      f('note', 'text', 'নোট', 'Note', { max: 200 }),
      f('bizDate', 'date', 'দিন', 'Day', { server: true, idx: true }),
      f('voidedAt', 'datetime', 'বাতিল', 'Voided', { server: true }),
    ],
  },
  {
    entity: 'recharges', model: 'Recharge', op: 'recharge.create', kind: 'ledger', category: 'mobile_banking', tool: 'recharge_log', perm: 'sale_entry', labels: ['রিচার্জ', 'Recharge'], titleField: 'number', window: 'biz_date',
    fields: [
      f('operator', 'enum', 'অপারেটর', 'Operator', { req: true, idx: true, ...opt(RECHARGE_OPERATORS, RECHARGE_OPERATORS.map((k) => OPERATOR_LABELS[k])) }),
      f('number', 'text', 'মোবাইল নম্বর', 'Mobile number', { req: true, max: 20 }),
      f('amount', 'money', 'অঙ্ক', 'Amount', { req: true }),
      f('commission', 'money', 'কমিশন', 'Commission', { def: '0' }),
      f('walletId', 'ref', 'যে ওয়ালেট থেকে', 'Paid from wallet', { ref: 'mfs_wallets' }),
      f('occurredAt', 'datetime', 'সময়', 'Time', { req: true, idx: true }),
      f('note', 'text', 'নোট', 'Note', { max: 200 }),
      f('bizDate', 'date', 'দিন', 'Day', { server: true, idx: true }),
      f('voidedAt', 'datetime', 'বাতিল', 'Voided', { server: true }),
    ],
  },
  {
    entity: 'mfs_closings', model: 'MfsClosing', op: 'mfs_closing.upsert', kind: DOC, category: 'mobile_banking', tool: 'multi_mfs_balance', perm: 'daily_khata', labels: ['দৈনিক মিলান', 'Daily reconciliation'], titleField: 'bizDate',
    fields: [
      f('bizDate', 'date', 'তারিখ', 'Date', { req: true, idx: true }),
      f('cashExpected', 'money', 'হিসাবে নগদ', 'Cash expected', { req: true }),
      f('cashCounted', 'money', 'গুনে পাওয়া নগদ', 'Cash counted', { req: true }),
      f('floatSnapshot', 'json', 'দিনশেষে ফ্লোট', 'End-of-day floats'),
      f('note', 'text', 'নোট', 'Note', { max: 200 }),
    ],
  },

  // Restaurant & Cafe (R1.1) -------------------------------------------------
  {
    entity: 'dining_tables', model: 'DiningTable', op: 'dining_table.upsert', kind: DOC, category: 'restaurant', tool: 'table_order', perm: 'stock_edit', labels: ['টেবিল', 'Tables'], titleField: 'name',
    fields: [
      f('name', 'text', 'টেবিলের নাম', 'Table name', { req: true, max: 30 }),
      f('seats', 'int', 'আসন', 'Seats', { def: 4 }),
      f('section', 'text', 'এলাকা (ঐচ্ছিক)', 'Section (optional)', { max: 30 }),
      f('isActive', 'bool', 'চালু', 'Active', { def: true }),
    ],
  },
  {
    entity: 'rest_orders', model: 'RestOrder', op: 'rest_order.upsert', kind: DOC, category: 'restaurant', tool: 'table_order', perm: 'sale_entry', labels: ['অর্ডার', 'Orders'], titleField: 'orderNo',
    fields: [
      f('orderNo', 'text', 'অর্ডার নং', 'Order no.', { req: true, max: 20 }),
      f('tableId', 'ref', 'টেবিল', 'Table', { ref: 'dining_tables', idx: true }),
      f('orderType', 'enum', 'ধরন', 'Type', { def: 'dine_in', ...opt(['dine_in', 'takeaway', 'delivery'], [['টেবিলে', 'Dine-in'], ['টেক-অ্যাওয়ে', 'Takeaway'], ['ডেলিভারি', 'Delivery']]) }),
      f('status', 'enum', 'অবস্থা', 'Status', { def: 'open', idx: true, ...opt(['open', 'kitchen', 'served', 'paid', 'cancelled'], [['খোলা', 'Open'], ['কিচেনে', 'In kitchen'], ['পরিবেশিত', 'Served'], ['পরিশোধিত', 'Paid'], ['বাতিল', 'Cancelled']]) }),
      f('customerId', 'ref', 'কাস্টমার', 'Customer', { ref: 'customers' }),
      f('guestName', 'text', 'অতিথির নাম', 'Guest', { max: 80 }),
      f('discount', 'money', 'ছাড়', 'Discount', { def: '0' }),
      f('saleId', 'ref', 'বিক্রি', 'Sale', { ref: 'sales' }),
      f('openedAt', 'datetime', 'খোলার সময়', 'Opened', { req: true }),
      f('note', 'text', 'নোট', 'Note', { max: 200 }),
    ],
  },
  {
    entity: 'rest_order_items', model: 'RestOrderItem', op: 'rest_order_item.upsert', kind: DOC, category: 'restaurant', tool: 'table_order', perm: 'sale_entry', labels: ['অর্ডারের আইটেম', 'Order items'], titleField: 'name',
    fields: [
      f('orderId', 'ref', 'অর্ডার', 'Order', { req: true, ref: 'rest_orders', idx: true }),
      f('productId', 'ref', 'মেনুর আইটেম', 'Menu item', { ref: 'products' }),
      f('name', 'text', 'নাম', 'Name', { req: true, max: 120 }),
      f('qty', 'qty', 'পরিমাণ', 'Qty', { req: true, def: '1' }),
      f('unitPrice', 'money', 'দাম', 'Price', { req: true }),
      f('kitchenStatus', 'enum', 'কিচেন স্ট্যাটাস', 'Kitchen status', { def: 'new', idx: true, ...opt(['new', 'preparing', 'ready', 'served', 'cancelled'], [['নতুন', 'New'], ['তৈরি হচ্ছে', 'Preparing'], ['তৈরি', 'Ready'], ['পরিবেশিত', 'Served'], ['বাতিল', 'Cancelled']]) }),
      f('note', 'text', 'নোট (ঝাল কম…)', 'Note', { max: 100 }),
    ],
  },

  // Tailoring & Boutique (R1.1) ---------------------------------------------
  {
    entity: 'measurement_profiles', model: 'MeasurementProfile', op: 'measurement_profile.upsert', kind: DOC, category: 'tailoring', tool: 'measurement_records', perm: 'sale_entry', labels: ['মাপ', 'Measurements'], titleField: 'label',
    fields: [
      f('customerId', 'ref', 'কাস্টমার', 'Customer', { req: true, ref: 'customers', idx: true }),
      f('garment', 'enum', 'পোশাকের ধরন', 'Garment', { req: true, idx: true, ...opt(['shirt', 'pant', 'kameez', 'salwar', 'blouse', 'panjabi', 'suit', 'other'], [['শার্ট', 'Shirt'], ['প্যান্ট', 'Pant'], ['কামিজ', 'Kameez'], ['সালোয়ার', 'Salwar'], ['ব্লাউজ', 'Blouse'], ['পাঞ্জাবি', 'Panjabi'], ['স্যুট', 'Suit'], ['অন্যান্য', 'Other']]) }),
      f('label', 'text', 'নাম (যেমন: ঈদের পাঞ্জাবি)', 'Label', { max: 60 }),
      f('measures', 'json', 'মাপ', 'Measures'),
      f('note', 'text', 'নোট', 'Note', { max: 200 }),
    ],
  },
  {
    entity: 'tailoring_orders', model: 'TailoringOrder', op: 'tailoring_order.upsert', kind: DOC, category: 'tailoring', tool: 'orders_delivery', perm: 'sale_entry', labels: ['টেইলারিং অর্ডার', 'Tailoring orders'], titleField: 'orderNo',
    fields: [
      f('orderNo', 'text', 'অর্ডার নং', 'Order no.', { req: true, max: 20 }),
      f('customerId', 'ref', 'কাস্টমার', 'Customer', { req: true, ref: 'customers', idx: true }),
      f('items', 'json', 'পোশাক', 'Items'),
      f('total', 'money', 'মোট মূল্য', 'Total', { req: true, def: '0' }),
      f('advance', 'money', 'অগ্রিম (তথ্য)', 'Advance (info)', { def: '0' }),
      f('measurementId', 'ref', 'মাপ', 'Measurement', { ref: 'measurement_profiles' }),
      f('orderDate', 'date', 'অর্ডারের তারিখ', 'Order date', { req: true }),
      f('deliveryDate', 'date', 'ডেলিভারির তারিখ', 'Delivery date', { req: true, idx: true }),
      f('status', 'enum', 'অবস্থা', 'Status', { def: 'received', idx: true, ...opt(['received', 'cutting', 'stitching', 'ready', 'delivered', 'cancelled'], [['গৃহীত', 'Received'], ['কাটা হচ্ছে', 'Cutting'], ['সেলাই হচ্ছে', 'Stitching'], ['তৈরি', 'Ready'], ['ডেলিভারি হয়েছে', 'Delivered'], ['বাতিল', 'Cancelled']]) }),
      f('saleId', 'ref', 'বিক্রি', 'Sale', { ref: 'sales' }),
      f('note', 'text', 'নোট', 'Note', { max: 300 }),
    ],
  },

  // Real Estate (R1.2) --------------------------------------------------------
  {
    entity: 'properties', model: 'Property', op: 'property.upsert', kind: DOC, category: 'real_estate', tool: 'property_list', perm: 'stock_edit', labels: ['প্রপার্টি', 'Properties'], titleField: 'title',
    fields: [
      f('title', 'text', 'শিরোনাম', 'Title', { req: true, max: 120 }),
      f('propType', 'enum', 'ধরন', 'Type', { req: true, idx: true, ...opt(['flat', 'plot', 'house', 'commercial', 'land', 'other'], [['ফ্ল্যাট', 'Flat'], ['প্লট', 'Plot'], ['বাড়ি', 'House'], ['বাণিজ্যিক', 'Commercial'], ['জমি', 'Land'], ['অন্যান্য', 'Other']]) }),
      f('area', 'qty', 'আকার', 'Size'),
      f('areaUnit', 'enum', 'একক', 'Unit', { def: 'sft', ...opt(['sft', 'katha', 'decimal', 'bigha', 'acre'], [['বর্গফুট', 'sq ft'], ['কাঠা', 'Katha'], ['শতক', 'Decimal'], ['বিঘা', 'Bigha'], ['একর', 'Acre']]) }),
      f('address', 'text', 'ঠিকানা', 'Address', { max: 200 }),
      f('price', 'money', 'দাম', 'Price', { req: true }),
      f('status', 'enum', 'অবস্থা', 'Status', { def: 'available', idx: true, ...opt(['available', 'booked', 'sold', 'rented'], [['বিক্রয়ের জন্য', 'Available'], ['বুকড', 'Booked'], ['বিক্রি হয়েছে', 'Sold'], ['ভাড়া হয়েছে', 'Rented']]) }),
      f('ownerName', 'text', 'মালিকের নাম', 'Owner', { max: 80 }),
      f('ownerPhone', 'phone', 'মালিকের ফোন', 'Owner phone'),
      f('customerId', 'ref', 'ক্রেতা', 'Buyer', { ref: 'customers' }),
      f('saleId', 'ref', 'বিক্রি', 'Sale', { ref: 'sales' }),
      f('note', 'text', 'নোট', 'Note', { max: 300 }),
    ],
  },
  {
    entity: 'leads', model: 'Lead', op: 'lead.upsert', kind: DOC, category: 'real_estate', tool: 'leads', perm: 'sale_entry', labels: ['লিড', 'Leads'], titleField: 'name',
    fields: [
      f('name', 'text', 'নাম', 'Name', { req: true, max: 80 }),
      f('phone', 'phone', 'ফোন', 'Phone'),
      f('interest', 'text', 'কী খুঁজছেন', 'Interested in', { max: 200 }),
      f('propertyId', 'ref', 'প্রপার্টি', 'Property', { ref: 'properties' }),
      f('budget', 'money', 'বাজেট', 'Budget'),
      f('stage', 'enum', 'ধাপ', 'Stage', { def: 'new', idx: true, ...opt(['new', 'contacted', 'visit', 'negotiation', 'won', 'lost'], [['নতুন', 'New'], ['যোগাযোগ হয়েছে', 'Contacted'], ['সাইট ভিজিট', 'Visit'], ['দর-কষাকষি', 'Negotiation'], ['চুক্তি হয়েছে', 'Won'], ['হারিয়েছি', 'Lost']]) }),
      f('nextFollowUp', 'date', 'পরের ফলো-আপ', 'Next follow-up', { idx: true }),
      f('note', 'text', 'নোট', 'Note', { max: 300 }),
    ],
  },
  {
    entity: 'property_commissions', model: 'PropertyCommission', op: 'property_commission.upsert', kind: DOC, category: 'real_estate', tool: 'agent_commission', perm: 'sale_entry', labels: ['এজেন্ট কমিশন', 'Agent commission'], titleField: 'agentName',
    fields: [
      f('propertyId', 'ref', 'প্রপার্টি', 'Property', { ref: 'properties', idx: true }),
      f('agentName', 'text', 'এজেন্ট', 'Agent', { req: true, max: 80 }),
      f('amount', 'money', 'কমিশন', 'Commission', { req: true }),
      f('status', 'enum', 'অবস্থা', 'Status', { def: 'due', idx: true, ...opt(['due', 'paid'], [['দেওয়া বাকি', 'Due'], ['দেওয়া হয়েছে', 'Paid']]) }),
      f('paidAt', 'datetime', 'দেওয়ার সময়', 'Paid at'),
      f('note', 'text', 'নোট', 'Note', { max: 200 }),
    ],
  },

  // Software / IT (R1.2) ------------------------------------------------------
  {
    entity: 'projects', model: 'Project', op: 'project.upsert', kind: DOC, category: 'software', tool: 'project_milestones', perm: 'sale_entry', labels: ['প্রজেক্ট', 'Projects'], titleField: 'name',
    fields: [
      f('customerId', 'ref', 'ক্লায়েন্ট', 'Client', { req: true, ref: 'customers', idx: true }),
      f('name', 'text', 'প্রজেক্টের নাম', 'Project name', { req: true, max: 120 }),
      f('status', 'enum', 'অবস্থা', 'Status', { def: 'planning', idx: true, ...opt(['planning', 'active', 'on_hold', 'done', 'cancelled'], [['পরিকল্পনা', 'Planning'], ['চলছে', 'Active'], ['স্থগিত', 'On hold'], ['শেষ', 'Done'], ['বাতিল', 'Cancelled']]) }),
      f('budget', 'money', 'বাজেট', 'Budget', { def: '0' }),
      f('startDate', 'date', 'শুরু', 'Start'),
      f('dueDate', 'date', 'শেষ করার তারিখ', 'Due'),
      f('note', 'text', 'নোট', 'Note', { max: 300 }),
    ],
  },
  {
    entity: 'milestones', model: 'Milestone', op: 'milestone.upsert', kind: DOC, category: 'software', tool: 'project_milestones', perm: 'sale_entry', labels: ['মাইলস্টোন', 'Milestones'], titleField: 'title',
    fields: [
      f('projectId', 'ref', 'প্রজেক্ট', 'Project', { req: true, ref: 'projects', idx: true }),
      f('title', 'text', 'মাইলস্টোন', 'Milestone', { req: true, max: 120 }),
      f('amount', 'money', 'অঙ্ক', 'Amount', { req: true }),
      f('dueDate', 'date', 'তারিখ', 'Due', { idx: true }),
      f('status', 'enum', 'অবস্থা', 'Status', { def: 'planned', idx: true, ...opt(['planned', 'invoiced', 'paid'], [['পরিকল্পিত', 'Planned'], ['ইনভয়েস হয়েছে', 'Invoiced'], ['পেয়েছি', 'Paid']]) }),
      f('saleId', 'ref', 'ইনভয়েস', 'Invoice', { ref: 'sales' }),
      f('note', 'text', 'নোট', 'Note', { max: 200 }),
    ],
  },
  {
    entity: 'retainers', model: 'Retainer', op: 'retainer.upsert', kind: DOC, category: 'software', tool: 'retainer', perm: 'sale_entry', labels: ['মাসিক Retainer', 'Retainers'], titleField: 'title',
    fields: [
      f('customerId', 'ref', 'ক্লায়েন্ট', 'Client', { req: true, ref: 'customers', idx: true }),
      f('title', 'text', 'সেবার নাম', 'Service', { req: true, max: 120 }),
      f('monthlyAmount', 'money', 'মাসিক অঙ্ক', 'Monthly amount', { req: true }),
      f('nextInvoiceDate', 'date', 'পরের ইনভয়েসের তারিখ', 'Next invoice', { req: true, idx: true }),
      f('status', 'enum', 'অবস্থা', 'Status', { def: 'active', idx: true, ...opt(['active', 'paused', 'ended'], [['চলছে', 'Active'], ['বিরতি', 'Paused'], ['শেষ', 'Ended']]) }),
      f('lastSaleId', 'ref', 'শেষ ইনভয়েস', 'Last invoice', { ref: 'sales' }),
      f('note', 'text', 'নোট', 'Note', { max: 200 }),
    ],
  },

  // Agriculture (R1.2) ----------------------------------------------------------
  {
    entity: 'land_parcels', model: 'LandParcel', op: 'land_parcel.upsert', kind: DOC, category: 'agriculture', tool: 'land_parcels', perm: 'stock_edit', labels: ['জমির খণ্ড', 'Land parcels'], titleField: 'name',
    fields: [
      f('name', 'text', 'জমির নাম', 'Parcel name', { req: true, max: 80 }),
      f('area', 'qty', 'আয়তন', 'Area', { req: true }),
      f('areaUnit', 'enum', 'একক', 'Unit', { def: 'decimal', ...opt(['decimal', 'katha', 'bigha', 'acre', 'hectare'], [['শতক', 'Decimal'], ['কাঠা', 'Katha'], ['বিঘা', 'Bigha'], ['একর', 'Acre'], ['হেক্টর', 'Hectare']]) }),
      f('location', 'text', 'অবস্থান', 'Location', { max: 120 }),
      f('ownership', 'enum', 'মালিকানা', 'Ownership', { def: 'own', ...opt(['own', 'leased', 'shared'], [['নিজের', 'Own'], ['বর্গা/লিজ', 'Leased'], ['ভাগে', 'Shared']]) }),
      f('leaseCost', 'money', 'লিজের খরচ', 'Lease cost', { def: '0' }),
      f('note', 'text', 'নোট', 'Note', { max: 200 }),
    ],
  },
  {
    entity: 'crop_seasons', model: 'CropSeason', op: 'crop_season.upsert', kind: DOC, category: 'agriculture', tool: 'crop_season', perm: 'sale_entry', labels: ['ফসল ও মৌসুম', 'Crops & seasons'], titleField: 'crop',
    fields: [
      f('crop', 'text', 'ফসল', 'Crop', { req: true, max: 60 }),
      f('parcelId', 'ref', 'জমি', 'Parcel', { ref: 'land_parcels', idx: true }),
      f('season', 'enum', 'মৌসুম', 'Season', { def: 'boro', ...opt(['aus', 'aman', 'boro', 'rabi', 'kharif', 'year_round'], [['আউশ', 'Aus'], ['আমন', 'Aman'], ['বোরো', 'Boro'], ['রবি', 'Rabi'], ['খরিফ', 'Kharif'], ['সারা বছর', 'Year-round']]) }),
      f('status', 'enum', 'অবস্থা', 'Status', { def: 'planned', idx: true, ...opt(['planned', 'growing', 'harvested', 'closed'], [['পরিকল্পিত', 'Planned'], ['চাষ চলছে', 'Growing'], ['ফসল তোলা হয়েছে', 'Harvested'], ['বন্ধ', 'Closed']]) }),
      f('startDate', 'date', 'শুরু', 'Start'),
      f('harvestDate', 'date', 'ফসল তোলার তারিখ', 'Harvest'),
      f('expectedYield', 'qty', 'প্রত্যাশিত ফলন', 'Expected yield'),
      f('yieldUnit', 'text', 'ফলনের একক', 'Yield unit', { def: 'kg', max: 12 }),
      f('note', 'text', 'নোট', 'Note', { max: 300 }),
    ],
  },

  // Personal Savings (R1.1) -----------------------------------------------------
  {
    entity: 'personal_entries', model: 'PersonalEntry', op: 'personal_entry.create', kind: 'ledger', category: 'personal_savings', tool: null, perm: 'sale_entry', labels: ['আয়-ব্যয়', 'Income & expense'], titleField: 'category', window: 'biz_date',
    fields: [
      f('entryType', 'enum', 'ধরন', 'Type', { req: true, idx: true, ...opt(['income', 'expense'], [['আয়', 'Income'], ['খরচ', 'Expense']]) }),
      f('category', 'text', 'উৎস / খাত', 'Source / category', { req: true, max: 60 }),
      f('amount', 'money', 'অঙ্ক', 'Amount', { req: true }),
      f('moneyAccountId', 'ref', 'কোন জায়গা থেকে', 'Account', { req: true, ref: 'money_accounts' }),
      f('note', 'text', 'নোট', 'Note', { max: 200 }),
      f('bizDate', 'date', 'তারিখ', 'Date', { idx: true, hint: ['খালি রাখলে আজকের তারিখ', 'Empty = today'] }),
      f('voidedAt', 'datetime', 'বাতিল', 'Voided', { server: true }),
    ],
  },
  {
    entity: 'savings_goals', model: 'SavingsGoal', op: 'savings_goal.upsert', kind: DOC, category: 'personal_savings', tool: null, perm: 'sale_entry', labels: ['সঞ্চয়ের লক্ষ্য', 'Savings goals'], titleField: 'name',
    fields: [
      f('name', 'text', 'লক্ষ্যের নাম', 'Goal', { req: true, max: 80 }),
      f('targetAmount', 'money', 'লক্ষ্য (৳)', 'Target (৳)', { req: true }),
      f('savedAmount', 'money', 'জমেছে (৳)', 'Saved (৳)', { def: '0' }),
      f('targetDate', 'date', 'কবের মধ্যে', 'By when'),
      f('accountId', 'ref', 'যে জায়গায় জমছে', 'Kept in', { ref: 'money_accounts' }),
      f('status', 'enum', 'অবস্থা', 'Status', { def: 'active', idx: true, ...opt(['active', 'done', 'cancelled'], [['চলছে', 'Active'], ['পূর্ণ', 'Done'], ['বাতিল', 'Cancelled']]) }),
      f('note', 'text', 'নোট', 'Note', { max: 200 }),
    ],
  },
  {
    entity: 'loans', model: 'Loan', op: 'loan.create', kind: 'ledger', category: 'personal_savings', tool: null, perm: 'sale_entry', labels: ['ধার', 'Loans'], titleField: 'party',
    fields: [
      f('direction', 'enum', 'ধরন', 'Direction', { req: true, idx: true, ...opt(['lent', 'borrowed'], [['ধার দিয়েছি', 'I lent'], ['ধার নিয়েছি', 'I borrowed']]) }),
      f('party', 'text', 'কাকে / কার কাছ থেকে', 'Person', { req: true, max: 80 }),
      f('partyPhone', 'phone', 'ফোন', 'Phone'),
      f('amount', 'money', 'অঙ্ক', 'Amount', { req: true }),
      f('moneyAccountId', 'ref', 'টাকা যে জায়গা থেকে/জায়গায়', 'Account', { ref: 'money_accounts' }),
      f('dueDate', 'date', 'ফেরতের তারিখ', 'Due', { idx: true }),
      f('note', 'text', 'নোট', 'Note', { max: 200 }),
      f('balance', 'money', 'বাকি', 'Remaining', { def: '0', server: true }),
      f('status', 'enum', 'অবস্থা', 'Status', { def: 'open', idx: true, server: true, ...opt(['open', 'settled'], [['চলছে', 'Open'], ['মিটেছে', 'Settled']]) }),
      f('bizDate', 'date', 'তারিখ', 'Date', { server: true }),
    ],
  },
  {
    entity: 'loan_repayments', model: 'LoanRepayment', op: 'loan.repay', kind: 'ledger', category: 'personal_savings', tool: null, perm: 'sale_entry', labels: ['ধার ফেরত', 'Loan repayments'], titleField: 'amount',
    fields: [
      f('loanId', 'ref', 'ধার', 'Loan', { req: true, ref: 'loans', idx: true }),
      f('amount', 'money', 'অঙ্ক', 'Amount', { req: true }),
      f('moneyAccountId', 'ref', 'জায়গা', 'Account', { ref: 'money_accounts' }),
      f('note', 'text', 'নোট', 'Note', { max: 200 }),
      f('bizDate', 'date', 'তারিখ', 'Date', { server: true }),
    ],
  },
] as const;

export const MODULE_BY_ENTITY = new Map(MODULE_ENTITIES.map((e) => [e.entity, e]));
export const MODULE_BY_OP = new Map(MODULE_ENTITIES.map((e) => [e.op, e]));
/** upsert-style document ops generated from the spec (ledger ops are hand-written) */
export const DOC_MODULES = MODULE_ENTITIES.filter((e) => e.kind === 'doc');
export const modulesOf = (c: Category): EntitySpec[] => MODULE_ENTITIES.filter((e) => e.category === c);
export const clientFields = (e: EntitySpec): FieldSpec[] => e.fields.filter((x) => !x.server);

/** Sale / expense link to a crop season (Dev Guide §7.2: ফসল বিক্রি = Sale আর seasonId; ইনপুট খরচ = Expense আর seasonId) */
export const SEASON_LINKED_TABLES = ['sales', 'expenses'] as const;

/** Default expense categories a farm gets at creation (Bible 3.13: সার, বীজ, কীটনাশক …) */
export const AGRI_EXPENSE_CATEGORIES = ['বীজ', 'সার', 'কীটনাশক', 'সেচ', 'শ্রমিক মজুরি', 'জমির লিজ', 'যন্ত্রপাতি ও জ্বালানি', 'পরিবহন'] as const;
export const PERSONAL_INCOME_CATEGORIES = ['বেতন', 'ব্যবসা', 'বোনাস', 'ভাড়া', 'উপহার', 'অন্যান্য আয়', 'মুনাফা / সুদ'] as const;
export const PERSONAL_EXPENSE_CATEGORIES = ['বাজার', 'বাসা ভাড়া', 'বিল (গ্যাস/বিদ্যুৎ/নেট)', 'যাতায়াত', 'চিকিৎসা', 'শিক্ষা', 'ঈদ ও উৎসব', 'বিনোদন', 'দান', 'অন্যান্য খরচ'] as const;
/**
 * The stored category is always the Bangla word (old data, imports and other devices agree on it); the English app shows this
 * label instead (v10 — "expense categories always display in Bangla"). A category typed by hand is shown as typed.
 */
export const PERSONAL_CATEGORY_EN: Readonly<Record<string, string>> = {
  'বেতন': 'Salary', 'ব্যবসা': 'Business', 'বোনাস': 'Bonus', 'ভাড়া': 'Rent received', 'উপহার': 'Gift', 'অন্যান্য আয়': 'Other income', 'মুনাফা / সুদ': 'Profit / interest',
  'বাজার': 'Groceries', 'বাসা ভাড়া': 'House rent', 'বিল (গ্যাস/বিদ্যুৎ/নেট)': 'Bills (gas / power / internet)', 'যাতায়াত': 'Transport', 'চিকিৎসা': 'Medical',
  'শিক্ষা': 'Education', 'ঈদ ও উৎসব': 'Eid & festivals', 'বিনোদন': 'Entertainment', 'দান': 'Charity', 'অন্যান্য খরচ': 'Other expense',
};
export const personalCategoryLabel = (c: string | null | undefined, lang: 'bn' | 'en' = 'bn'): string => (!c ? '' : lang === 'en' ? PERSONAL_CATEGORY_EN[c] ?? c : c);
